/** Camara o galeria hacia adjuntos (max 10 MB, tipos del servidor). */
import { useEffect, useRef, useState } from 'react';
import { useUploadLimits } from '@/hooks/useUploadLimits';
import { MobileIcon } from './MobileIcon';

const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip';

type FileCaptureProps = {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
};

export function FileCapture({ onFiles, disabled = false }: FileCaptureProps) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<File[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const previewsRef = useRef<Record<string, string>>({});
  previewsRef.current = previews;
  const limits = useUploadLimits();
  const maxSizeBytes = limits.attachmentMaxSizeMb * 1024 * 1024;

  useEffect(
    () => () => {
      for (const url of Object.values(previewsRef.current)) {
        URL.revokeObjectURL(url);
      }
    },
    [],
  );

  function handle(list: FileList | null) {
    const files = Array.from(list ?? []);
    const remaining = Math.max(0, limits.attachmentBatchMaxFiles - picked.length);
    const oversized = files.filter((file) => file.size > maxSizeBytes);
    if (oversized.length > 0) {
      setError(
        `"${oversized[0].name}" supera el maximo de ${limits.attachmentMaxSizeMb} MB`,
      );
      return;
    }
    if (files.length > remaining) {
      setError(
        `Puedes adjuntar hasta ${limits.attachmentBatchMaxFiles} archivos por lote`,
      );
      return;
    }
    const valid = files.slice(0, remaining);
    if (valid.length === 0) return;
    setError(null);
    const next = [...picked, ...valid];
    setPicked(next);
    setPreviews((prev) => {
      const updated = { ...prev };
      for (const file of valid) {
        if (file.type.startsWith('image/') && !updated[file.name]) {
          updated[file.name] = URL.createObjectURL(file);
        }
      }
      return updated;
    });
    onFiles(next);
  }

  function remove(name: string) {
    const targetUrl = previews[name];
    if (targetUrl) {
      URL.revokeObjectURL(targetUrl);
      setPreviews((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
    const next = picked.filter((file) => file.name !== name);
    setPicked(next);
    onFiles(next);
  }

  function iconFor(file: File): 'picture_as_pdf' | 'folder_open' {
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      return 'picture_as_pdf';
    }
    return 'folder_open';
  }

  return (
    <div className="rounded-card bg-white p-4 shadow-card">
      <input
        ref={cameraRef}
        type="file"
        multiple
        accept="image/*"
        capture="environment"
        disabled={disabled}
        className="sr-only"
        aria-label="Tomar foto con la cámara"
        onChange={(e) => {
          handle(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={galleryRef}
        type="file"
        multiple
        accept={ACCEPT}
        disabled={disabled}
        className="sr-only"
        aria-label={`Adjuntar fotos o archivos, maximo ${limits.attachmentMaxSizeMb} MB y ${limits.attachmentBatchMaxFiles} archivos`}
        onChange={(e) => {
          handle(e.target.files);
          e.target.value = '';
        }}
      />
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => cameraRef.current?.click()}
          className="flex min-h-[56px] items-center justify-center gap-2 rounded-full bg-primary-light font-semibold text-primary transition-all hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60"
        >
          <MobileIcon name="photo_camera" className="h-6 w-6" />
          Cámara
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => galleryRef.current?.click()}
          className="flex min-h-[56px] items-center justify-center gap-2 rounded-full bg-bg-tertiary font-semibold text-text-secondary transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60"
        >
          <MobileIcon name="photo_library" className="h-6 w-6" />
          Galería
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-2 break-words text-sm text-error">
          {error}
        </p>
      )}
      {picked.length > 0 && (
        <ul className="mt-2 space-y-2">
          {picked.map((file) => (
            <li key={file.name} className="flex min-h-[44px] items-center gap-2 text-sm text-text-secondary">
              {file.type.startsWith('image/') && previews[file.name] ? (
                <img
                  src={previews[file.name]}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-lg object-cover"
                />
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-text-secondary">
                  <MobileIcon name={iconFor(file)} className="h-6 w-6" />
                </span>
              )}
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Quitar ${file.name}`}
                onClick={() => remove(file.name)}
                className="min-h-[44px] shrink-0 rounded-full bg-bg-tertiary px-3 py-2 text-xs font-semibold text-text-secondary transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Quitar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
